import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Package, Plus, Truck, Info, AlertTriangle } from "lucide-react";
import { useOrderShipments, useShipmentSummary, useDealCreditConfig, OrderShipment } from "@/hooks/use-order-shipments";
import { ShipmentCard } from "./ShipmentCard";
import { CreateShipmentDialog } from "./CreateShipmentDialog";

interface ShipmentManagementSectionProps {
  orderId: string;
  allocatedDeviceIds: string[];
  totalCredits: number;
  orderStatus: string;
  onGenerateDeliveryNote?: (shipment: OrderShipment) => void;
  isGeneratingDeliveryNoteForShipment?: string | null;
}

export const ShipmentManagementSection = ({
  orderId,
  allocatedDeviceIds,
  totalCredits,
  orderStatus,
  onGenerateDeliveryNote,
  isGeneratingDeliveryNoteForShipment
}: ShipmentManagementSectionProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  
  const { data: shipments, isLoading } = useOrderShipments(orderId);
  const { data: dealConfig } = useDealCreditConfig(orderId);
  const summary = useShipmentSummary(orderId);

  // Calculate what's already allocated to shipments
  const allocatedToShipments = useMemo(() => {
    if (!shipments) return { devices: [] as string[], credits: 0 };
    
    const devices = shipments.flatMap(s => s.allocated_device_ids);
    const credits = shipments.reduce((sum, s) => sum + s.allocated_credits, 0);
    
    return { devices, credits };
  }, [shipments]);

  // Calculate what's available for new shipments
  const availableDevices = useMemo(() => {
    return allocatedDeviceIds.filter(id => !allocatedToShipments.devices.includes(id));
  }, [allocatedDeviceIds, allocatedToShipments.devices]);

  const availableCredits = totalCredits - allocatedToShipments.credits;

  // Multi-year contract calculations
  const coverageYears = dealConfig?.coverageYears || 1;
  const annualCreditsPerCamera = dealConfig?.annualCreditsPerCamera || 0;
  const isMultiYear = coverageYears > 1;
  
  // Calculate reserved future credits (Year 2+ credits for shipped devices)
  const shippedDeviceCount = allocatedToShipments.devices.length;
  const reservedFutureCredits = isMultiYear && shippedDeviceCount > 0
    ? (coverageYears - 1) * shippedDeviceCount * annualCreditsPerCamera
    : 0;
  
  // For multi-year: only show "unallocated" for devices, not future year credits
  // For single-year: show the old behavior (both devices and credits as unallocated)
  const hasUnallocatedDevices = availableDevices.length > 0;
  const hasReservedFutureCredits = reservedFutureCredits > 0;
  
  // Year range text for reserved credits (e.g., "Years 2-3")
  const futureYearsText = coverageYears === 2 ? "Year 2" : `Years 2-${coverageYears}`;

  // Show during order_processing for incremental allocation, plus fulfillment stages
  const canManageShipments = [
    'order_processing',
    'in_fulfillment', 
    'ready_for_shipping', 
    'shipped', 
    'delivered'
  ].includes(orderStatus);

  if (!canManageShipments) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center text-muted-foreground">
            Loading shipments...
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = summary.totalShipments > 0 
    ? ((summary.deliveredCount + summary.confirmedCount) / summary.totalShipments) * 100 
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Shipment Management
            {summary.hasShipments && (
              <Badge variant="outline" className="ml-2">
                {summary.totalShipments} shipment{summary.totalShipments !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
          
          {(availableDevices.length > 0 || availableCredits > 0) && (
            <Button
              size="sm"
              onClick={() => setIsCreateDialogOpen(true)}
              className="flex items-center gap-1"
              style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
            >
              <Plus className="h-4 w-4" />
              Add Shipment
            </Button>
          )}
        </div>
        
        {summary.hasShipments && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Delivery Progress</span>
              <span className="font-medium">
                {summary.deliveredCount + summary.confirmedCount} / {summary.totalShipments} delivered
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary stats */}
        {summary.hasShipments && (
          <div className="grid grid-cols-4 gap-4 p-3 bg-muted/30 rounded-lg text-center">
            <div>
              <p className="text-2xl font-bold">{summary.pendingCount}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.shippedCount}</p>
              <p className="text-xs text-muted-foreground">In Transit</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.deliveredCount}</p>
              <p className="text-xs text-muted-foreground">Delivered</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.confirmedCount}</p>
              <p className="text-xs text-muted-foreground">Confirmed</p>
            </div>
          </div>
        )}

        {/* Shipment cards */}
        {shipments && shipments.length > 0 ? (
          <div className="space-y-3">
            {shipments.map((shipment) => (
              <ShipmentCard 
                key={shipment.id} 
                shipment={shipment}
                onGenerateDeliveryNote={onGenerateDeliveryNote}
                isGeneratingDeliveryNote={isGeneratingDeliveryNoteForShipment === shipment.id}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <h4 className="font-medium text-muted-foreground">No Shipments Yet</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Create shipments to allocate and ship devices incrementally. 
              You can fulfill the order in multiple deliveries.
            </p>
            {(availableDevices.length > 0 || availableCredits > 0) && (
              <Button
                className="mt-4"
                onClick={() => setIsCreateDialogOpen(true)}
                style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Shipment
              </Button>
            )}
          </div>
        )}

        {/* Unallocated devices warning (only for devices not yet in shipments) */}
        {hasUnallocatedDevices && summary.hasShipments && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>Unallocated items:</strong>{' '}
                {availableDevices.length} device{availableDevices.length !== 1 ? 's' : ''}
                {!isMultiYear && availableCredits > 0 && `, ${availableCredits.toLocaleString()} credits`}
              </span>
            </p>
          </div>
        )}

        {/* Reserved future year credits info (for multi-year contracts) */}
        {hasReservedFutureCredits && summary.hasShipments && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <Info className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>Future agreement year credits reserved:</strong>{' '}
                {reservedFutureCredits.toLocaleString()} credits ({futureYearsText})
              </span>
            </p>
          </div>
        )}
      </CardContent>

      <CreateShipmentDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        orderId={orderId}
        availableDevices={availableDevices}
        totalCredits={totalCredits}
        allocatedCredits={allocatedToShipments.credits}
      />
    </Card>
  );
};
